
import TenantAdminTab from '@/components/tenant/TenantAdminTab.vue'
import TenantBrandingTab from '@/components/tenant/TenantBrandingTab.vue'
import TenantContentTab from '@/components/tenant/TenantContentTab.vue'
import { SubscriptionConst, TenantConst } from '@/constants'
import { BillingSource } from '@/modules/subscription/types'
import { Permissions } from '@/security/permissions'
import { PermissionActions } from '@/store/constants'
import { FlexFeatureField, Tenant, TenantSubscription } from '@/store/modules/app/types'
import { DynaconfConfig, Namespace, PricignListItem, PricingTier } from '@/store/types'
import { AppHelper } from '@/utils/app-helper'
import { stripHexAlphaChannel } from '@/utils/color-helpers'
import { HSHelpers } from '@/utils/hs-helpers'
import { ValidationRules } from '@/utils/input-validation'
import { TenantHelpers } from '@/utils/tenant-helpers'
import IconPalette from '@svg/Icon_Palette.svg'
import IconSettings from '@svg/Icon_Settings.svg'
import IconSpark from '@svg/Icon_Spark.svg'
import IconUser from '@svg/IconUser.svg'
import { Component, Vue, Watch } from 'vue-property-decorator'
import { Action, Getter, State } from 'vuex-class'

@Component({
  components: {
    TenantAdminTab,
    TenantBrandingTab,
    TenantContentTab,
    IconSpark,
    IconPalette,
    IconSettings,
    IconUser,
  },
})
export default class TenantVue extends Vue {
  @State('apiError', { namespace: Namespace.Utils })
  public apiError!: string
  @State('myTenantIndex', { namespace: Namespace.App })
  public myTenantIndex!: number
  @State('myTenants', { namespace: Namespace.App })
  public myTenants!: Tenant[]
  @State('myTenant', { namespace: Namespace.App })
  public myTenant!: Tenant
  @State('dynaconfConfig', { namespace: Namespace.App })
  public dynaconfConfig!: DynaconfConfig
  @State('isSuperUser', { namespace: Namespace.App })
  public isSuperUser!: boolean
  @State('initializing', { namespace: Namespace.Utils })
  public initializing!: boolean

  @Getter('isPreparingApp', { namespace: Namespace.Utils })
  public isPreparingApp!: boolean

  @Getter('isJigSparkOptIn', { namespace: Namespace.App })
  public isJigSparkOptIn!: boolean

  @Action('loadMyTenant', { namespace: Namespace.App })
  public loadMyTenant: any
  @Action('loadMyTenantLogo', { namespace: Namespace.App })
  public loadMyTenantLogo: any
  @Action('updateMyTenant', { namespace: Namespace.App })
  public updateMyTenant: any
  @Action('updateMyTenantsWithNewTenant', { namespace: Namespace.App })
  public updateMyTenantsWithNewTenant: any
  @Action('getDynaconfConfig', { namespace: Namespace.App })
  public getDynaconfConfig: any
  @Action('loadTenantUseID', { namespace: Namespace.App })
  public loadTenantUseID: any

  public $refs!: Vue['$refs'] & {
    contentTab: TenantContentTab
    brandingTab: TenantBrandingTab
    adminTab: TenantAdminTab
  }

  private readonly saveChangesSuccessMsg = 'Team changes saved'
  private readonly defaultColor1Hex = TenantHelpers.TenantDefaultColor1Hex

  private currentTab: number = 0
  private editedTenant!: Tenant
  private nameRules!: any
  private urlRules!: any
  private noneNegativeNumberRules!: any
  private snackbarVisible: boolean = false
  private snackbarMessage: string = this.saveChangesSuccessMsg
  private isSaving: boolean = false
  private isAppleBillingSource: boolean = false
  private openHSChatFailed: boolean = false

  private currentSubscription: TenantSubscription = SubscriptionConst.defaultSubscription
  private subscriptionTiersValue: SubscriptionConst.Subscription[] = SubscriptionConst.subscriptionTiers
  private subscriptionManagedByTypeValue: any[] = SubscriptionConst.subscriptionManagedByTypes
  private pricingSetsValue: SubscriptionConst.Subscription[] = SubscriptionConst.pricingSets
  private dynaconfPricingListNames: SubscriptionConst.Subscription[] = []
  private allowMovoJigs: boolean = false
  private allowJigStats: boolean = false
  private AVPFeatureGateEnabled: boolean = true

  private customFeaturesDefaultValues: Record<string, any> = {}
  private customFeaturesModels: Record<string, any> = {}
  private customFeaturesFlexSwitches: Record<string, FlexFeatureField> = {}
  private customFeaturesFlexTextFields: Record<string, FlexFeatureField> = {}
  private crmOtherEditableModels: Record<string, string | boolean> = {}
  private crmOtherEditableSwitches: Record<string, FlexFeatureField> = {}
  private crmOtherEditableTextFields: Record<string, FlexFeatureField> = {}

  constructor() {
    super()
    this.nameRules = ValidationRules.RequiredName
    this.urlRules = ValidationRules.OptionalUrl
    this.noneNegativeNumberRules = ValidationRules.RequiredNoneNegativeNumber

    this.currentSubscription.SubscriptionFeatures.avp.gatedFeatures = {
      [SubscriptionConst.Features.AVPFullAccessFeatureGate]: {
        name: 'ViewingGate_MAR_2024',
        enforce: false,
      },
    }
    this.editedTenant = { ...TenantConst.defaultTenant }
  }

  @Watch('myTenantIndex')
  private onMyTenantIndexPropertyChanged(value: number, oldValue: number) {
    this.loadData()
    if (this.$refs.brandingTab) {
      this.$refs.brandingTab.clearLogoImageData()
    }
  }

  @Watch('initializing')
  private onInitializingChanged(value: boolean) {
    if (!value) {
      this.loadData()
    }
  }

  protected async mounted() {
    if (!this.initializing) {
      this.loadData()
    }
  }

  private get allowCustombranding(): boolean {
    return this.editedTenant.Subscription.AllowCustomBranding
  }

  // True when the current user is not a super user and lacks Manage permission on this tenant
  private get lacksTenantManagePermission(): boolean {
    if (this.editedTenant.ID === TenantHelpers.InvalidTenantID) {
      return true
    }

    if (this.isSuperUser) {
      return false
    }

    const tenantTokens = this.$auth0.tenants
    const tenantToken = TenantHelpers.GetTenantTokenByTenantID(tenantTokens, this.editedTenant.ID)
    return tenantToken === undefined || !Permissions.TokenHasPermissionAction(tenantToken, Permissions.PermTenant, PermissionActions.Manage)
  }

  // User can update branding (team settings) when:
  // 1. user is a Super User (JigStaff only)
  // 2. AllowCustomBranding is true and user has team manage permission (Team Manager / Team Admin)
  private get customBrandingDisabled(): boolean {
    if (!this.allowCustombranding && !this.isSuperUser) {
      return true
    }

    if (this.lacksTenantManagePermission) {
      return true
    }

    return this.isSaving
  }

  private get targetTenant(): Tenant | undefined {
    if (this.myTenants.length === 0 || this.myTenantIndex === TenantHelpers.InvalidTenantIndex) {
      return
    }

    return this.myTenants[this.myTenantIndex]
  }

  private get defaultPricingSetConfig(): any {
    if (this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet] == null) {
      return {}
    }

    return this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet].tiers[this.editedTenant.Subscription.TierType]
  }

  private async loadData() {
    this.refreshEditedTenant()

    // Check billing source for the Apple billing banner
    if (this.editedTenant.ID !== TenantHelpers.InvalidTenantID) {
      const tenantTokens = this.$auth0.tenants
      const thisTenantToken = TenantHelpers.GetTenantTokenByTenantID(tenantTokens, this.editedTenant.ID)
      if (thisTenantToken) {
        this.isAppleBillingSource = thisTenantToken.billing_source === BillingSource.Apple
      }
    }

    // The tenant image endpoint returns 400 when this tenant has no logo path.
    // Guard the logo request to avoid failing the whole page load for logo-less tenants.
    if (this.editedTenant.ID !== TenantHelpers.InvalidTenantID && this.editedTenant.Logourl !== '') {
      await this.loadMyTenantLogo(this.editedTenant)
      this.refreshEditedTenant()
    }

    await this.loadTenantUseID({ tenantId: this.editedTenant.ID })
    this.refreshEditedTenant()

    if (this.editedTenant.ID !== TenantHelpers.InvalidTenantID && this.$refs.contentTab) {
      await this.$refs.contentTab.loadTenantContextDocumentsPage(1)
    }

    if (Object.keys(this.dynaconfConfig.pricing.sets).length === 0) {
      await this.getDynaconfConfig()
    }

    if (Object.keys(this.dynaconfConfig.pricing.sets).length > 0) {
      this.dynaconfPricingListNames = Object.keys(this.dynaconfConfig.pricing.sets) as SubscriptionConst.Subscription[]
      this.pricingSetsValue = [SubscriptionConst.Subscription.pricingSet_2020_01, ...this.dynaconfPricingListNames]
    }

    if (this.$refs.brandingTab) {
      this.$refs.brandingTab.updateDynaconfPricingListNames(this.dynaconfPricingListNames)
      this.$refs.brandingTab.loadSubscription()
    }
    this.currentSubscription = {
      ...this.editedTenant.Subscription,
    }

    // Refresh the default values after dynaconf is loaded
    this.updateCustomFeaturesDefaultValues()
  }

  private refreshEditedTenant() {
    if (this.targetTenant == null) {
      return
    }

    // Use JSON.stringfy and JSON.parse for a deep copy of the value not reference.
    // So that any unsaved changes won't update the original `tenant` item of `state.myTenants` array and `state.myTenant` object.
    // When using Object.assign, top-level properties are assigned by value, however nested objects/references are still shared between original `tenant` and `editedTenant`
    // Note that as our tenant object is relatively simple, we can use JSON parse/stringify. If tenant object contains non-JSON-safe value (e.g., functions, undefined, NaN).
    this.editedTenant = AppHelper.deepCopyNestedJsonSafeObject(this.targetTenant)

    this.allowMovoJigs = this.editedTenant.Subscription.SubscriptionFeatures.movo.enabled
    this.allowJigStats = this.editedTenant.Subscription.SubscriptionFeatures.stats.jigStats.enabled
    // By default for new tenants or existing tenants that
    // haven't updated this flag before
    // `enabled` and `enforce` values will both be `true`.
    // We check `enforce` value for actual Featuregated status.
    this.AVPFeatureGateEnabled =
      this.editedTenant.Subscription.SubscriptionFeatures.avp != null &&
      this.editedTenant.Subscription.SubscriptionFeatures.avp.gatedFeatures != null &&
      this.editedTenant.Subscription.SubscriptionFeatures.avp.gatedFeatures.ViewingGate_MAR_2024.enforce

    // Correct any invalid colors set as "" or "      "
    if (!this.editedTenant.ColorHex1 || !this.editedTenant.ColorHex1.trim()) {
      this.editedTenant.ColorHex1 = TenantHelpers.TenantDefaultColor1Hex
    } else {
      this.editedTenant.ColorHex1 = stripHexAlphaChannel(this.editedTenant.ColorHex1)
    }
    if (!this.editedTenant.ColorHex2 || !this.editedTenant.ColorHex2.trim()) {
      this.editedTenant.ColorHex2 = TenantHelpers.TenantDefaultColor2Hex
    } else {
      this.editedTenant.ColorHex2 = stripHexAlphaChannel(this.editedTenant.ColorHex2)
    }

    this.setupCRMOtherEditableModels()

    if (this.editedTenant.Subscription.SubscriptionFeatures.Available == null) {
      return
    }

    // Iterate over all available flex fields, separate out
    // switch fields and text-input fields.
    // Also exclude special field `MaxJigs` as it needs to sit in a special layout.
    for (const key in this.editedTenant.Subscription.SubscriptionFeatures.Available) {
      const field: FlexFeatureField = this.editedTenant.Subscription.SubscriptionFeatures.Available[key]

      if (field.Type === SubscriptionConst.FlexFeatureFieldType.bool) {
        this.customFeaturesFlexSwitches[key] = field
      } else if (key.toLowerCase() !== 'maxjigs') {
        // We are excluding MaxJigs here because it has a special layout.
        // We render everything else as dynamic fields.
        this.customFeaturesFlexTextFields[key] = field
      }

      // Set the v-model variable using `customFeaturesModels`
      // Vue.set is required so dynamically added keys are reactive in Vue 2
      if (field.SyncField && this.editedTenant.Subscription.SubscriptionFeatures.CustomFeatures[field.SyncField] != null) {
        // If custom value is set use the value from CustomFeatures
        Vue.set(this.customFeaturesModels, key, this.editedTenant.Subscription.SubscriptionFeatures.CustomFeatures[field.SyncField])
      } else if (field.SyncField && this.editedTenant.Subscription[field.SyncField] != null) {
        // If no custom value is set, use the value from Sync field.
        Vue.set(this.customFeaturesModels, key, this.editedTenant.Subscription[field.SyncField])
      } else {
        // If SyncField is not available, then the value will be available in CustomFeatures.
        Vue.set(this.customFeaturesModels, key, this.editedTenant.Subscription.SubscriptionFeatures.CustomFeatures[key])
      }

      if (Object.keys(this.defaultPricingSetConfig).length > 0) {
        this.updateCustomFeatureDefaultValue(field, key)
      }
    }
  }

  private updateCustomFeaturesDefaultValues() {
    for (const key in this.editedTenant.Subscription.SubscriptionFeatures.Available) {
      const field: FlexFeatureField = this.editedTenant.Subscription.SubscriptionFeatures.Available[key]

      this.updateCustomFeatureDefaultValue(field, key)
    }
  }

  private setupCRMOtherEditableModels() {
    this.crmOtherEditableModels = {}
    this.crmOtherEditableSwitches = {}
    this.crmOtherEditableTextFields = {}

    if (this.editedTenant.CRM == null || this.editedTenant.CRM.Available == null || this.editedTenant.CRM.DynamicData == null) {
      return
    }

    const dynamicCRMData = this.editedTenant.CRM.DynamicData as Record<string, any>
    const crmOtherEditableFieldKeys = TenantConst.CRMOtherEditableFields as Array<keyof Tenant['CRM']['Available']>

    for (const key of crmOtherEditableFieldKeys) {
      const field = this.editedTenant.CRM.Available[key]
      if (!field) {
        continue
      }

      const currentValue = dynamicCRMData[key as string]

      if (field.Type === SubscriptionConst.FlexFeatureFieldType.bool) {
        this.crmOtherEditableSwitches[key as string] = field
        this.crmOtherEditableModels[key as string] = typeof currentValue === 'boolean' ? currentValue : Boolean(field.Default)
        continue
      }

      if (
        field.Type === SubscriptionConst.FlexFeatureFieldType.string ||
        field.Type === SubscriptionConst.FlexFeatureFieldType.string_array
      ) {
        this.crmOtherEditableTextFields[key as string] = field
      } else {
        continue
      }

      if (field.Type === SubscriptionConst.FlexFeatureFieldType.string_array) {
        if (Array.isArray(currentValue)) {
          this.crmOtherEditableModels[key as string] = currentValue.join(', ')
        } else if (typeof currentValue === 'string') {
          this.crmOtherEditableModels[key as string] = currentValue
        } else {
          this.crmOtherEditableModels[key as string] = ''
        }
        continue
      }

      this.crmOtherEditableModels[key as string] = currentValue != null ? String(currentValue) : ''
    }
  }

  private persistCRMOtherEditableValues(updatedTenant: Tenant) {
    if (!updatedTenant.CRM || !updatedTenant.CRM.Available || !updatedTenant.CRM.DynamicData) {
      return
    }

    const dynamicCRMData = updatedTenant.CRM.DynamicData as Record<string, any>
    const crmOtherEditableFieldKeys = TenantConst.CRMOtherEditableFields as Array<keyof Tenant['CRM']['Available']>

    for (const key of crmOtherEditableFieldKeys) {
      const field = updatedTenant.CRM.Available[key]
      if (!field) {
        continue
      }

      const rawValue = this.crmOtherEditableModels[key as string]

      if (field.Type === SubscriptionConst.FlexFeatureFieldType.bool) {
        dynamicCRMData[key as string] = Boolean(rawValue)
        continue
      }

      if (field.Type === SubscriptionConst.FlexFeatureFieldType.string_array) {
        if (Array.isArray(rawValue)) {
          dynamicCRMData[key as string] = rawValue.map((item) => String(item).trim()).filter((item) => item.length > 0)
          continue
        }

        if (typeof rawValue !== 'string') {
          dynamicCRMData[key as string] = []
          continue
        }

        dynamicCRMData[key as string] = rawValue
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
        continue
      }

      dynamicCRMData[key as string] = rawValue != null ? String(rawValue) : ''
    }
  }

  private updateCustomFeatureDefaultValue(field: FlexFeatureField, key: string) {
    // Save the default value using `customFeaturesDefaultValues`
    if (field.SyncField) {
      // Since dynaconf returns camelCase and our
      // flex fields keys are in SentenseCase,
      // we need to check key value in both format.
      // For example `TeamStorageLimitMB` and `teamStorageLimitMB`, `MaxJigs` and `maxJigs`.
      this.customFeaturesDefaultValues[key] =
        this.defaultPricingSetConfig[field.SyncField] != null
          ? this.defaultPricingSetConfig[field.SyncField]
          : this.defaultPricingSetConfig[AppHelper.sentenceToCamelCase(field.SyncField)]
    } else {
      this.customFeaturesDefaultValues[key] = this.editedTenant.Subscription.SubscriptionFeatures.Available[key].Default
    }
  }

  private async saveChanges(updatedTenant: Tenant) {
    if (this.customBrandingDisabled) {
      return
    }

    this.isSaving = true
    this.snackbarVisible = false

    const tenantCopy = AppHelper.deepCopyNestedJsonSafeObject(updatedTenant)

    tenantCopy.ColorHex1 = stripHexAlphaChannel(tenantCopy.ColorHex1)
    tenantCopy.ColorHex2 = stripHexAlphaChannel(tenantCopy.ColorHex2)

    const tenantPricingSet: PricignListItem = this.dynaconfConfig.pricing.sets[tenantCopy.Subscription.PricingSet]

    if (tenantPricingSet) {
      const tenantPricingSetTier: PricingTier = tenantPricingSet.tiers[tenantCopy.Subscription.TierType]

      if (tenantPricingSetTier.seatsIncluded != null) {
        tenantCopy.Subscription.SeatsIncludedInTier = tenantPricingSetTier.seatsIncluded
      } else {
        // API will handle the correct SeatsIncludedInTier number.
        tenantCopy.Subscription.SeatsIncludedInTier = SubscriptionConst.Subscription.defaultSeatsIncludedInTier
      }
      if (tenantPricingSetTier.maxJigs != null) {
        tenantCopy.Subscription.MaxJigs = tenantPricingSetTier.maxJigs
      } else {
        // API will handle the correct MaxJigs.
        tenantCopy.Subscription.MaxJigs = SubscriptionConst.Subscription.defaultMaxJigsInTier
      }
      if (tenantPricingSetTier.maxJigsCountPer != null) {
        tenantCopy.Subscription.MaxJigsCountPer = tenantPricingSetTier.maxJigsCountPer
      } else {
        // API will handle the correct MaxJigsCountPer.
        tenantCopy.Subscription.MaxJigsCountPer = SubscriptionConst.Subscription.defaultMaxJigsPerInTier
      }
    }

    if (tenantCopy.Subscription.AdditionalSeats && typeof tenantCopy.Subscription.AdditionalSeats !== 'number') {
      tenantCopy.Subscription.AdditionalSeats = parseInt(tenantCopy.Subscription.AdditionalSeats, 10)
    }

    // Append url protocol if there isn't one.
    if (tenantCopy.JigFinishedCustomLink && !tenantCopy.JigFinishedCustomLink.includes('://')) {
      tenantCopy.JigFinishedCustomLink = `http://${tenantCopy.JigFinishedCustomLink}`
    }

    // Update features
    // For AVP full access, we follow following logic:
    // `Full AVP access enabled` === `AVPFeatureGate not shown` === enabled: true, enforce: false`
    // `Full AVP access disabled` === `AVPFeatureGate shown` === enabled: false, enforce: true`
    tenantCopy.Subscription.SubscriptionFeatures.avp.enabled = !this.AVPFeatureGateEnabled
    if (tenantCopy.Subscription.SubscriptionFeatures.avp.gatedFeatures) {
      tenantCopy.Subscription.SubscriptionFeatures.avp.gatedFeatures[SubscriptionConst.Features.AVPFullAccessFeatureGate].enforce =
        this.AVPFeatureGateEnabled
    }
    tenantCopy.Subscription.SubscriptionFeatures.movo.enabled = this.allowMovoJigs
    tenantCopy.Subscription.SubscriptionFeatures.stats.jigStats.enabled = this.allowJigStats

    // Update legacy values. When API is fixed we can remove them.
    tenantCopy.Subscription.AllowMovoJigs = this.allowMovoJigs
    tenantCopy.Subscription.AllowJigStats = this.allowJigStats

    for (const key in this.customFeaturesModels) {
      const defaultValue: any = this.customFeaturesDefaultValues[key]
      let value: any = this.customFeaturesModels[key]
      const featureField: FlexFeatureField = tenantCopy.Subscription.SubscriptionFeatures.Available[key]

      switch (featureField.Type) {
        case SubscriptionConst.FlexFeatureFieldType.int:
          value = parseInt(value, 10)
          break
        case SubscriptionConst.FlexFeatureFieldType.float:
          value = parseFloat(value)
          break
        default:
          break
      }

      // Remove the custom value if it matches the pricing set's default
      if (value === defaultValue) {
        delete tenantCopy.Subscription.SubscriptionFeatures.CustomFeatures[key]
        continue
      }

      tenantCopy.Subscription.SubscriptionFeatures.CustomFeatures[key] = value
    }

    this.persistCRMOtherEditableValues(tenantCopy)

    await this.updateMyTenant(tenantCopy)

    if (this.apiError === undefined || this.apiError.length === 0) {
      this.snackbarVisible = true
      await this.loadMyTenant({ TenantId: this.editedTenant.ID })
      this.editedTenant = AppHelper.deepCopyNestedJsonSafeObject(this.myTenant)

      if (this.isSuperUser) {
        this.updateMyTenantsWithNewTenant(this.editedTenant)
      }
    }

    this.refreshEditedTenant()
    this.isSaving = false
  }

  private showSnackbar(message: string) {
    this.snackbarMessage = message
    this.snackbarVisible = true
  }

  private closeSnackbar() {
    this.snackbarVisible = false
  }

  private openHSChat() {
    this.openHSChatFailed = !HSHelpers.OpenHSChat()
  }

  private updateDataFromTier() {
    // If subscription tier has changed, show `-` as Seats Included,
    // as client side has no details of seats included number,
    // it's handled by API. If subscription tier changed back to initial
    // value, show original SeatsIncluded number.
    if (this.editedTenant.Subscription.TierType === this.currentSubscription.TierType) {
      this.editedTenant.Subscription.SeatsIncludedInTier = this.currentSubscription.SeatsIncludedInTier
      this.editedTenant.Subscription.MaxJigs = this.currentSubscription.MaxJigs
      this.editedTenant.Subscription.MaxJigsCountPer = this.currentSubscription.MaxJigsCountPer
    } else {
      const tenantPricingSet: PricignListItem = this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet]
      const tenantPricingSetTier: PricingTier = tenantPricingSet.tiers[this.editedTenant.Subscription.TierType]

      this.editedTenant.Subscription.SeatsIncludedInTier =
        tenantPricingSetTier.seatsIncluded || SubscriptionConst.Subscription.defaultSeatsIncludedInTier
      this.editedTenant.Subscription.MaxJigs = tenantPricingSetTier.maxJigs || SubscriptionConst.Subscription.defaultMaxJigsInTier
      this.editedTenant.Subscription.MaxJigsCountPer =
        tenantPricingSetTier.maxJigsCountPer || SubscriptionConst.Subscription.defaultMaxJigsPerInTier
    }
  }
}
